import { GridApi } from 'ag-grid-community';
import React from 'react';
import { useDispatch } from 'react-redux';
import { AxiosError } from 'axios';
import { useUpdateStopClassificationBulk } from '@clean-code/api/hooks';
import { showAnnotateStopErrorModal } from '@clean-code/ducks/modal';
import { getAxiosErrorMessage } from '@clean-code/utils/errorHandling';
import { TouchedStop } from '@clean-code/types/Stop';
import type { StopAnnotationChange } from '@clean-code/utils/stopAnnotation';
import {
  EWorkflow,
  useWorkflowTracker,
  type WorkflowOrigin
} from '@clean-code/analytics/workflow';

type UseUpdateStopOptions = {
  gridApi?: GridApi<TouchedStop>;
  /** Overwrite the annotation even if someone else changed it meanwhile. */
  forceUpdate?: boolean;
  /** Page the annotation is tracked under; omit to skip tracking. */
  origin?: WorkflowOrigin;
  /**
   * Off where a session-level event already counts the successful
   * annotation (realtime's annotate modal); failures are always tracked.
   */
  trackSuccess?: boolean;
};

export function useUpdateStop({
  gridApi,
  forceUpdate = false,
  origin,
  trackSuccess = true
}: UseUpdateStopOptions = {}): (
  stop: TouchedStop,
  change?: StopAnnotationChange
) => void {
  const updateStopBulk = useUpdateStopClassificationBulk();
  const dispatch = useDispatch();
  const tracker = useWorkflowTracker(
    origin?.source ?? 'realtime',
    EWorkflow.AnnotateStops,
    { tab: origin?.tab }
  );
  return React.useCallback(
    (stop: TouchedStop, change?: StopAnnotationChange) => {
      if (!stop) return;
      const changesPayload = mapStopToPayload(stop, forceUpdate);
      const trackCompleted = (success: boolean) =>
        origin &&
        (trackSuccess || !success) &&
        tracker.trackCompleted(success, {
          machine_id: stop.machineId,
          machine_name: stop.machine,
          items_count: 1,
          changed_fields: change?.changedFields,
          is_first_annotation: change?.isFirstAnnotation,
          is_forced: forceUpdate
        });
      updateStopBulk.mutate(changesPayload, {
        onSuccess: () => {
          trackCompleted(true);
          gridApi
            ?.getRowNode(stop.stopId.toString())
            ?.setData({ ...stop, touched: false });
        },
        onError: (error) => {
          trackCompleted(false);
          gridApi
            ?.getRowNode(stop.stopId.toString())
            ?.setData({ ...stop, touched: false });

          if (isOverwritingStopClassificationError(error as AxiosError)) {
            dispatch(
              showAnnotateStopErrorModal({
                stopInfo: stop,
                error: error,
                origin,
                trackSuccess
              })
            );
          }
        }
      });
    },
    [
      updateStopBulk,
      gridApi,
      dispatch,
      forceUpdate,
      origin,
      trackSuccess,
      tracker
    ]
  );
}

function mapStopToPayload(change: TouchedStop, forceUpdate?: boolean) {
  const changesPayload = [
    {
      machineId: change.machineId,
      startTime: change.stopStartTime,
      ...(change.classification.generalCauseId
        ? { generalCauseId: change.classification.generalCauseId }
        : {}),
      ...(change.classification.specificCauseId
        ? { specificCauseId: change.classification.specificCauseId }
        : {}),
      ...(change.classification.componentId
        ? { componentId: change.classification.componentId }
        : {}),
      description: change.classification.description,
      previousUpdatedAt: forceUpdate
        ? undefined
        : change.classification.updatedAt
    }
  ];
  return changesPayload;
}

function isOverwritingStopClassificationError(error: AxiosError) {
  return (
    getAxiosErrorMessage(error) === 'Overwritting stop classification error'
  );
}
